/**
 * Contact / lead capture.
 *
 * House style is the catalogue's (see app/pages/Datasets/ui.tsx): monospace for
 * every label, hairline borders on a flat near-black ground, section labels
 * notched into the top border, and no transitions — hover and focus switch
 * instantly, so there is nothing for prefers-reduced-motion to suppress.
 *
 * The EmailJS wiring, field `name` attributes, required-field logic, character
 * counter and toasts are unchanged from the previous version; only the
 * presentation moved.
 */
import React, { FormEvent, useState } from 'react';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';

/** Page ground. Repeated on the notch labels so they interrupt the hairline. */
const GROUND = 'bg-[#0d1117]';

/** Bordered panel with its label notched into the top border. */
function Panel({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`relative border border-gray-800 ${className}`}>
      <span
        className={`absolute -top-[7px] left-3 ${GROUND} px-1.5 font-mono text-[11px] uppercase tracking-wider text-gray-400`}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * One hairline field. Focus is a 1px outline that appears instantly — another
 * pass fixed focus indicators site-wide, so nothing here animates or removes
 * them.
 */
const FIELD =
  'block w-full rounded-none border border-gray-800 bg-black/20 px-2.5 py-1.5 text-sm text-white ' +
  'placeholder:text-gray-400 hover:border-gray-700 ' +
  'focus:border-blue-400 focus:outline focus:outline-1 focus:outline-offset-1 focus:outline-blue-400 focus:ring-0';

const LABEL =
  'mb-1 block font-mono text-[11px] uppercase tracking-wider text-gray-400';

function Required() {
  return (
    <span aria-hidden="true" className="text-blue-400">
      *
    </span>
  );
}

/** Plain key/value pair — contact details as data, not as a marketing line. */
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="border-b border-gray-800 px-3 py-2 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-sm text-white">{value}</div>
    </div>
  );
}

export const ContactSection: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const maxMessageLength = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.sendForm(
        'service_p2gn1hf',
        'template_h6cmv5d',
        e.currentTarget,
        'TCryOR4_bl0OJJ8-g',
      );

      toast.success('Message sent successfully!');
      (e.target as HTMLFormElement).reset();
      setMessageLength(0);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('EmailJS Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(e.target.value.length);
  };

  return (
    <section id="contact" className={`w-full ${GROUND} py-12`}>
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <header className="mb-6">
          <h2 className="font-mono text-2xl font-semibold text-white">
            Talk to us
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-400">
            Questions about coverage, licensing or a dataset that is not in the
            catalogue yet — send them here.
          </p>
        </header>

        {/* Contact details as a plain data strip, not decorated marketing. */}
        <div className="mb-8 grid grid-cols-1 border border-gray-800 sm:grid-cols-3">
          <Fact
            label="phone"
            value={
              <a
                href="tel:+16179992099"
                className="text-white hover:text-blue-300 focus:outline focus:outline-1 focus:outline-offset-2 focus:outline-blue-400"
              >
                +1 (617) 999-2099
              </a>
            }
          />
          <Fact label="response" value="1–2 business days" />
          <Fact label="hours" value="Mon–Fri, 09:00–18:00 ET" />
        </div>

        <Panel label="message" className="px-4 pb-4 pt-6 sm:px-5 sm:pb-5">
          <form onSubmit={handleSubmit} className="grid gap-x-6 md:grid-cols-2">
            {/* Left column: who is asking. */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="from_name" className={LABEL}>
                    Name <Required />
                  </label>
                  <input
                    type="text"
                    id="from_name"
                    name="from_name"
                    required
                    className={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="reply_to" className={LABEL}>
                    Email <Required />
                  </label>
                  <input
                    type="email"
                    id="reply_to"
                    name="reply_to"
                    required
                    className={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="company" className={LABEL}>
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className={LABEL}>
                    Phone
                  </label>
                  <input type="tel" id="phone" name="phone" className={FIELD} />
                </div>
                <div>
                  <label htmlFor="reason" className={LABEL}>
                    Reason
                  </label>
                  <select id="reason" name="reason" className={FIELD}>
                    <option value="">Select a reason</option>
                    <option value="Demo Request">Demo Request</option>
                    <option value="Product Information">
                      Product Information
                    </option>
                    <option value="Pricing">Pricing</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="preferred_time" className={LABEL}>
                    Preferred time
                  </label>
                  <select
                    id="preferred_time"
                    name="preferred_time"
                    className={FIELD}
                  >
                    <option value="">Select a preferred time</option>
                    <option value="Morning">Morning</option>
                    <option value="Afternoon">Afternoon</option>
                    <option value="Evening">Evening</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Right column: what they are asking. */}
            <div className="mt-3 flex flex-col md:mt-0">
              <label htmlFor="message" className={LABEL}>
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                maxLength={maxMessageLength}
                onChange={handleMessageChange}
                className={`${FIELD} resize-y`}
                placeholder="Tell us about your investment operations and goals"
              />
              <div className="mt-1 text-right font-mono text-[11px] tabular-nums text-gray-400">
                {messageLength}/{maxMessageLength}
              </div>
            </div>

            {/*
              Consent and submit run the full width under both columns, so the
              two columns are free to be different heights without leaving a
              hole in the panel.
            */}
            <div className="mt-4 flex flex-col gap-3 border-t border-dashed border-gray-800 pt-3 sm:flex-row sm:items-start sm:justify-between md:col-span-2">
              <div className="flex items-start gap-2.5">
                <input
                  id="privacy"
                  name="privacy"
                  type="checkbox"
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 rounded-none border-gray-700 bg-black/20 text-blue-400 focus:outline focus:outline-1 focus:outline-offset-2 focus:outline-blue-400 focus:ring-0"
                />
                <div className="max-w-2xl">
                  <label
                    htmlFor="privacy"
                    className="font-mono text-[11px] uppercase tracking-wider text-gray-400"
                  >
                    I agree to the privacy policy <Required />
                  </label>
                  <p className="mt-1 text-[11px] leading-snug text-gray-400">
                    Your data is secured with industry-standard encryption and
                    will never be shared with third parties. By submitting this
                    form, you consent to our privacy policy and allow us to
                    contact you regarding your inquiry.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="shrink-0 rounded-none border border-gray-300 bg-gray-100 px-4 py-2 font-mono text-xs uppercase tracking-wider text-[#0d1117] hover:bg-white focus:outline focus:outline-1 focus:outline-offset-2 focus:outline-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        </Panel>

        {/*
          The zcal embed is a 650px third-party iframe that used to occupy half
          the section and load on every homepage view. It is now behind a
          disclosure and only mounted once opened — the form is the primary
          path, booking is the secondary one, and neither costs the other space.
        */}
        <div className="mt-6">
          <button
            type="button"
            aria-expanded={bookingOpen}
            aria-controls="booking-panel"
            onClick={() => setBookingOpen(open => !open)}
            className="rounded-none border border-gray-800 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-gray-400 hover:border-gray-700 hover:text-white focus:outline focus:outline-1 focus:outline-offset-2 focus:outline-blue-400"
          >
            {bookingOpen ? '− Hide booking calendar' : '+ Book a call'}
          </button>

          {bookingOpen && (
            <Panel label="booking" className="mt-4 px-3 pb-3 pt-6">
              <div id="booking-panel">
                <iframe
                  src="https://zcal.co/i/bbPjg_CJ?embed=1&embedType=iframe"
                  loading="lazy"
                  style={{
                    border: 'none',
                    minWidth: '280px',
                    width: '100%',
                    height: '620px',
                  }}
                  id="zcal-invite"
                  scrolling="no"
                  title="Schedule a Meeting"
                />
              </div>
            </Panel>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
